<script setup lang="ts">
import { computed, ref } from 'vue';
import { AxiosError } from 'axios';
import type { OfferTeamSize } from '@models/offer';
import Icon from '@components/Icon.vue';
import Button from '@components/Button.vue';
import { useLoading } from '@composables/useLoading';
import requests from '@tools/requests';
import { URL_OFFER_FAVORITE_SET } from '@tools/routes/routes';
import { flashBagError } from '@tools/flashbag';
import Logger from '@tools/logger/logger';
import { storeCallbackAndDoLogin } from '../favorite-bus-handler';

const props = withDefaults(defineProps<{
  isActive?: boolean,
  offerHashId: string,
  offerTeamSizes?: OfferTeamSize[],
}>(), {
  isActive: false,
});

const emit = defineEmits<{(event: 'change', newIsFavorite: boolean): Promise<void>;
}>();

const { isLoading, stopLoading, startLoading } = useLoading();

const isFavoriteActive = ref<boolean>(props.isActive);

const offerTeamSizes = computed<OfferTeamSize[]>(() => props.offerTeamSizes ?? [ { hash_id: props.offerHashId, team_size: null } ]);

const changeFavorite = async (): Promise<void> => {
  startLoading();

  try {
    await requests.post(
      URL_OFFER_FAVORITE_SET.replace('{id}', props.offerHashId),
      JSON.stringify({
        favorite: !isFavoriteActive.value,
        offerTeamSizes: offerTeamSizes.value.map((offerTeamSize) => ({
          hashId: offerTeamSize.hash_id,
          teamSize: offerTeamSize.team_size,
        })),
      }),
    );
  } catch (error: unknown) {
    if (error instanceof AxiosError && error.response?.status === 401) {
      storeCallbackAndDoLogin(!isFavoriteActive.value, props.offerHashId);
      stopLoading();
      return;
    }

    flashBagError();
    Logger.error(error);
    stopLoading();
    return;
  }

  isFavoriteActive.value = !isFavoriteActive.value;

  await emit('change', isFavoriteActive.value);

  stopLoading();
};
</script>

<template>
  <Button
    variant="circle"
    class="favorite-button"
    :class="{ 'favorite-button--active': isFavoriteActive }"
    :disabled="isLoading"
    @click.prevent="changeFavorite"
  >
    <Icon
      v-if="isFavoriteActive"
      name="ph_heart-straight-fill"
      fill="red"
    />

    <Icon v-else name="ph_heart-straight-bold" fill="current" />
  </Button>
</template>

<style scoped lang="scss">
@use '../../../styles/variables' as *;
@use '../../../styles/mixins' as *;

.favorite-button {
  margin: 0;
  height: rem(36);
  width: rem(36);
}

.favorite-button:hover {
  color: $colorRed;
}
</style>
